import { useState } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  CircularProgress,
} from '@mui/material';
import { authApi } from '../../api/auth_api';
import { ApiError } from '../../api/utils';

// Opened from the reset email: /password/set/:key/:uuid
// (FHPasswordResetToken.get_password_set_link in the backend).
export const PasswordSetPage = () => {
  const { key, uuid } = useParams<{ key: string; uuid: string }>();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linkInvalid, setLinkInvalid] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      await authApi.resetPassword(key ?? '', uuid ?? '', password);
      navigate('/login', {
        replace: true,
        state: { notice: 'Your password has been set. Sign in with your new password.' },
      });
    } catch (err) {
      if (err instanceof ApiError && err.message.includes('invalid or has expired')) {
        setLinkInvalid(true);
      } else {
        setError(err instanceof Error ? err.message : 'Unable to set the password');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 400, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Set New Password
          </Typography>

          {linkInvalid ? (
            <>
              <Alert severity="error" sx={{ mb: 2 }}>
                This reset link is invalid or has expired. Links work once, for 15
                minutes.
              </Alert>
              <Button
                component={RouterLink}
                to="/password/reset"
                fullWidth
                variant="contained"
                size="large"
                sx={{ mb: 2 }}
              >
                Send a New Link
              </Button>
            </>
          ) : (
            <Box component="form" onSubmit={handleSubmit}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <TextField
                fullWidth
                label="New Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                margin="normal"
                required
                autoComplete="new-password"
                autoFocus
                helperText="At least 8 characters"
              />

              <TextField
                fullWidth
                label="Confirm New Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                margin="normal"
                required
                autoComplete="new-password"
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{ mt: 3, mb: 2 }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Set Password'}
              </Button>
            </Box>
          )}

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2">
              <Link component={RouterLink} to="/login">
                Back to Sign In
              </Link>
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
